import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HardHat, Zap, Users, Truck, Camera, Clock, ShieldCheck, WifiOff, CheckCircle2, ArrowRight, MapPin, Star, Menu, X, LogIn, FolderOpen, Plus, Minus } from "lucide-react";
import { publicApi } from "@/lib/api";
import { fmtEUR, calcYearlySavings } from "@/lib/billing";
import { useAuth } from "@/context/AuthContext";

const NAV_LINKS = [
  { href: "#kenmerken", label: "Kenmerken" },
  { href: "#waarom", label: "Waarom" },
  { href: "#prijzen", label: "Prijzen" },
  { href: "#faq", label: "FAQ" },
];

export default function Marketing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [period, setPeriod] = useState("month"); // month | year
  const [menu, setMenu] = useState(false);
  const [extraSeats, setExtraSeats] = useState({}); // { start: 2, pro: 0, max: 0 }
  const isLoggedIn = user && user.id;
  const appHome = user?.role === "UITVOERDER" ? "/vandaag" : "/planbord";

  const bumpSeats = (tier, delta) => {
    setExtraSeats((prev) => {
      const cur = prev[tier] || 0;
      const next = Math.max(0, Math.min(50, cur + delta));
      return { ...prev, [tier]: next };
    });
  };

  useEffect(() => {
    document.title = "Ploegbord — Stuur al je ploegen aan vanaf één scherm";
    const meta = document.querySelector('meta[name="description"]');
    const desc = "Nederlandse planningsapp voor bouwbedrijven met kleine ploegen. Planbord voor kantoor, Vandaag-app voor de bouwplaats. 7 dagen gratis proberen.";
    if (meta) meta.setAttribute("content", desc);
    else {
      const m = document.createElement("meta"); m.name = "description"; m.content = desc; document.head.appendChild(m);
    }
  }, []);

  useEffect(() => {
    publicApi.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden" data-testid="marketing-page">
      {/* NAV */}
      <header className="sticky top-0 z-40 bg-slate-900 border-b-4 border-orange-600 shadow-[0_4px_0_rgba(0,0,0,0.15)]">
        <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="marketing-logo">
            <img src="/logo-ploegbord.webp?v=8" alt="Ploegbord" className="h-14 md:h-16 w-auto"/>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="font-black uppercase tracking-widest text-xs text-white hover:text-orange-400 transition">{l.label}</a>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            {isLoggedIn ? (
              <Link to={appHome} className="pb-btn-primary h-10 leading-[36px] flex items-center gap-1" data-testid="nav-app">
                <ArrowRight size={16}/> Naar de app
              </Link>
            ) : (
              <>
                <Link to="/login" className="h-10 leading-[36px] flex items-center gap-1 px-4 rounded font-black uppercase tracking-widest text-xs text-white border-2 border-white hover:bg-white hover:text-slate-900 transition" data-testid="nav-login">
                  <LogIn size={16}/> Inloggen
                </Link>
                <Link to="/signup" className="pb-btn-primary h-10 leading-[36px] flex items-center gap-1" data-testid="nav-signup">
                  <Zap size={16}/> Registreren
                </Link>
              </>
            )}
          </div>
          <button className="md:hidden text-white" onClick={() => setMenu((v) => !v)} aria-label="Menu">{menu ? <X size={24}/> : <Menu size={24}/>}</button>
        </div>
        {menu && (
          <div className="md:hidden bg-slate-900 border-t-2 border-orange-600 px-4 py-3 space-y-2">
            {NAV_LINKS.map((l) => <a key={l.href} href={l.href} onClick={() => setMenu(false)} className="block py-2 font-black uppercase tracking-widest text-xs text-white">{l.label}</a>)}
            {isLoggedIn ? (
              <Link to={appHome} className="pb-btn-primary w-full text-center flex items-center justify-center gap-1" data-testid="nav-app-mobile">
                <ArrowRight size={16}/> Naar de app
              </Link>
            ) : (
              <>
                <Link to="/login" className="w-full text-center flex items-center justify-center gap-1 h-10 rounded border-2 border-white text-white font-black uppercase tracking-widest text-xs" data-testid="nav-login-mobile">
                  <LogIn size={16}/> Inloggen
                </Link>
                <Link to="/signup" className="pb-btn-primary w-full text-center flex items-center justify-center gap-1" data-testid="nav-signup-mobile">
                  <Zap size={16}/> Registreren
                </Link>
              </>
            )}
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.08]" style={{ backgroundImage: "radial-gradient(#F97316 1.2px, transparent 1.2px)", backgroundSize: "22px 22px" }}/>
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24 grid lg:grid-cols-2 gap-10 items-center relative min-w-0">
          <div>
            <div className="inline-flex items-center gap-2 border-2 border-orange-600 rounded-full px-3 py-1 text-xs font-black uppercase tracking-widest bg-slate-900 text-orange-400">
              <span className="w-2 h-2 rounded-full bg-orange-500"/> Voor Nederlandse bouwbedrijven
            </div>
            <h1 className="pb-heading text-3xl sm:text-5xl lg:text-6xl font-black mt-4 leading-[1.05] tracking-tight text-white break-words">
              Stuur al je ploegen aan<br/>vanaf <span className="text-orange-500">één scherm</span>.
            </h1>
            <p className="mt-5 text-lg text-slate-300 font-semibold max-w-xl">
              Planbord voor kantoor, Vandaag-app voor de bouwplaats. Uren, materieel en foto&apos;s kloppen aan het einde van de dag — zonder appjes, spreadsheets of rondbellen.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/signup" className="pb-btn-primary flex items-center justify-center gap-2 text-base !min-h-[56px] !px-6" data-testid="hero-cta-signup">
                <Zap size={20}/> 7 dagen gratis proberen
              </Link>
              <a href="#kenmerken" className="h-14 px-6 rounded font-black uppercase tracking-widest text-sm border-2 border-white text-white flex items-center justify-center gap-2 hover:bg-white hover:text-slate-900 transition" data-testid="hero-cta-demo">
                Bekijk demo <ArrowRight size={18}/>
              </a>
            </div>
            <div className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-400 break-words">Geen betalingsinfo nodig · Maandelijks opzegbaar</div>
          </div>

          {/* Mock planbord */}
          <div className="relative w-full min-w-0">
            <div className="pb-card p-3 shadow-[8px_8px_0_#0F172A] overflow-x-auto">
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="pb-heading font-black text-sm">PLANBORD — DEZE WEEK</div>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500"/><div className="w-2 h-2 rounded-full bg-amber-500"/><div className="w-2 h-2 rounded-full bg-green-500"/>
                </div>
              </div>
              <div className="grid grid-cols-[80px_repeat(5,minmax(52px,1fr))] gap-[1px] bg-slate-300 border-2 border-slate-900 text-[10px] font-bold min-w-[360px]">
                <div className="bg-slate-900 text-white p-1.5">PLOEG</div>
                {["MA","DI","WO","DO","VR"].map((d) => <div key={d} className="bg-slate-900 text-white p-1.5 text-center">{d}</div>)}
                {[
                  { n: "Frits", blocks: [{ c:"#EA580C", t:"Renovatie" }, { c:"#3B82F6", t:"Nieuwbouw" }, { c:"#EA580C", t:"Renovatie" }, { c:"#16A34A", t:"Aanbouw" }, null] },
                  { n: "Jordi L.",   blocks: [{ c:"#3B82F6", t:"Nieuwbouw" }, { c:"#3B82F6", t:"Nieuwbouw" }, { c:"#EA580C", t:"Renovatie" }, null, { c:"#EA580C", t:"Renovatie" }] },
                  { n: "Dennis B.", blocks: [null, { c:"#16A34A", t:"Aanbouw" }, { c:"#16A34A", t:"Aanbouw" }, { c:"#16A34A", t:"Aanbouw" }, { c:"#3B82F6", t:"Nieuwbouw" }] },
                ].map((row) => (
                  <React.Fragment key={row.n}>
                    <div className="bg-slate-50 p-1.5 font-black text-slate-900">{row.n}</div>
                    {row.blocks.map((b, i) => (
                      <div key={i} className="bg-white p-1 min-h-[36px]">
                        {b && <div className="text-white px-1.5 py-1 rounded border border-slate-900" style={{ background: b.c }}>{b.t}</div>}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-6 -right-4 rotate-3 hidden md:block">
              <div className="pb-card p-3 w-56 shadow-[6px_6px_0_#0F172A]">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">VANDAAG · FRITS</div>
                <div className="pb-heading font-black">Renovatie Boslaan 12</div>
                <div className="text-xs text-slate-600 flex items-center gap-1 mt-1"><MapPin size={12}/> Waalwijk</div>
                <div className="mt-2 rounded border-2 border-orange-600 bg-orange-50 p-2 text-orange-700 font-black text-xl tabular-nums">02:14:07</div>
                <div className="text-[10px] font-bold text-slate-500 mt-1">Timer loopt · STOP knop actief</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEEM / OPLOSSING */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-orange-400 text-xs font-black uppercase tracking-widest">Zo werkt het nu vaak</div>
            <h2 className="pb-heading text-3xl md:text-5xl font-black mt-2">Weg met de losse appjes.</h2>
            <p className="mt-3 text-slate-300 font-semibold">Excel, WhatsApp en telefoontjes werken tot je bedrijf groeit. Daarna kost het je uren per week aan puzzelwerk.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-lg border-2 border-slate-700 bg-slate-800/60 p-6 shadow-[6px_6px_0_rgba(234,88,12,0.35)]">
              <div className="inline-block text-orange-400 text-[10px] font-black uppercase tracking-widest border border-orange-400 rounded px-2 py-1">Zonder Ploegbord</div>
              <h3 className="pb-heading text-2xl md:text-3xl font-black mt-3">Excel · WhatsApp · 06 bellen</h3>
              <ul className="mt-5 space-y-3 text-slate-300 font-semibold">
                {[
                  "Planning aanpassen in Excel en donderdagmiddag rondsturen — vrijdag zijn er alweer wijzigingen.",
                  "Losse appjes voor adressen, foto's en materiaallijstjes. Wie had waar afgesproken?",
                  "ZZP'ers erbij? Bellen. Bus meegeven? Bellen. Uren? Aan het eind van de maand puzzelen.",
                  "Op de bouwplaats weet niemand meer wat de laatste versie is.",
                ].map((t) => <li key={t} className="flex gap-2 items-start"><span className="text-orange-400 mt-1">✕</span>{t}</li>)}
              </ul>
            </div>
            <div className="rounded-lg border-2 border-orange-600 bg-slate-800 p-6 shadow-[6px_6px_0_#EA580C]">
              <div className="inline-block text-orange-400 text-[10px] font-black uppercase tracking-widest border border-orange-400 rounded px-2 py-1">Met Ploegbord</div>
              <h3 className="pb-heading text-2xl md:text-3xl font-black mt-3">Eén planbord, alles klopt.</h3>
              <ul className="mt-5 space-y-3 text-slate-100 font-semibold">
                {[
                  "Sleep taken op het planbord; iedereen ziet direct de nieuwste versie in de Vandaag-app.",
                  "Adressen, foto's en materialen zitten bij het project — niet in vier losse chats.",
                  "ZZP'ers plannen mee als gewone ploegleden. Bus toewijzen? Eén klik.",
                  "Start/Stop timer op de bouwplaats — uren rollen automatisch de administratie in.",
                ].map((t) => <li key={t} className="flex gap-2 items-start"><CheckCircle2 size={18} className="text-orange-400 mt-0.5 shrink-0"/>{t}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* KENMERKEN */}
      <section id="kenmerken" className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-orange-500 text-xs font-black uppercase tracking-widest">Alles wat je écht gebruikt</div>
            <h2 className="pb-heading text-3xl md:text-5xl font-black mt-2 text-white">Van planbord tot loonstrook.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {[
              { icon: Users,    t: "Centraal planbord",     d: "Drag-and-drop per ploeg en dag. Meerdere klussen op dezelfde dag mag — planner blijft in de lead." },
              { icon: HardHat,  t: "Vandaag-scherm",        d: "Mobiel-first voor de uitvoerder. Adres, foto's, materiaal en navigatie — één tap." },
              { icon: Clock,    t: "Uren per medewerker",   d: "Start/Stop timer op de bouwplaats. Week-, maand- en totalen per uitvoerder in één overzicht." },
              { icon: FolderOpen, t: "Uren per project",    d: "Zie in één klap hoeveel uur er op elk project is gemaakt en door wie — geen puzzelwerk aan het eind van de maand." },
              { icon: Truck,    t: "Bussen & materieel",    d: "Voertuigen en gereedschap gekoppeld aan planning. Wie rijdt met welke bus?" },
              { icon: Camera,   t: "Projectfoto's",         d: "Foto's plakken direct bij het project. Uitvoerder ziet ze in de Vandaag-app." },
              { icon: MapPin,   t: "Navigatie in 1 tap",    d: "Adres incl. huisnummer springt direct naar Google Maps." },
              { icon: WifiOff,  t: "Werkt met beperkt bereik", d: "De Vandaag-app blijft bruikbaar in een put of dubbeldiepte garage." },
              { icon: Zap,      t: "In één avond klaar",    d: "Geen consultants. Registreer, tik je ploeg in en morgen ligt er een planning." },
            ].map((f) => (
              <div key={f.t} className="rounded-lg border-2 border-slate-700 bg-slate-900 p-5 hover:border-orange-600 hover:shadow-[6px_6px_0_#EA580C] transition">
                <div className="w-10 h-10 border-2 border-orange-600 rounded flex items-center justify-center bg-slate-950 text-orange-500"><f.icon size={20}/></div>
                <div className="pb-heading font-black text-lg mt-3 text-white">{f.t}</div>
                <div className="text-sm text-slate-400 font-semibold mt-1">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WAAROM */}
      <section id="waarom" className="bg-orange-600/10 border-y-2 border-orange-600 py-16">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-orange-500 text-xs font-black uppercase tracking-widest">Waarom Ploegbord</div>
            <h2 className="pb-heading text-3xl md:text-4xl font-black mt-2 text-white">Voor de Nederlandse bouw. Geen ERP-ballast.</h2>
            <ul className="mt-6 space-y-3 font-semibold text-slate-200">
              <li className="flex gap-2 items-start"><CheckCircle2 className="text-orange-500 mt-0.5 shrink-0" size={18}/> ZZP&apos;ers plannen zoals medewerkers — geen aparte flow.</li>
              <li className="flex gap-2 items-start"><CheckCircle2 className="text-orange-500 mt-0.5 shrink-0" size={18}/> Uren per medewerker én per project — direct klaar voor de administratie.</li>
              <li className="flex gap-2 items-start"><CheckCircle2 className="text-orange-500 mt-0.5 shrink-0" size={18}/> Werkt op een oude bouwtelefoon en een nieuwe iPad.</li>
              <li className="flex gap-2 items-start"><CheckCircle2 className="text-orange-500 mt-0.5 shrink-0" size={18}/> Jij eigenaar van je data. Exporteren wanneer je wilt.</li>
            </ul>
          </div>
          <div className="rounded-lg border-2 border-slate-700 bg-slate-900 p-6">
            <div className="text-xs font-black uppercase tracking-widest text-slate-400">Deze week op het bord</div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <Stat n="82%" l="bezetting"/>
              <Stat n="27" l="planbare uren"/>
              <Stat n="6" l="open projecten"/>
            </div>
            <div className="mt-4 border-2 border-slate-700 rounded p-3 bg-slate-950">
              <div className="text-xs font-black uppercase tracking-widest text-slate-400">Uren deze week</div>
              <div className="mt-2 flex items-end justify-between h-24">
                {[38, 42, 40, 45, 32].map((v, i) => (
                  <div key={i} className="flex-1 mx-0.5 bg-orange-600 border-2 border-orange-800" style={{ height: `${v*2}%` }}/>
                ))}
              </div>
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1"><span>MA</span><span>DI</span><span>WO</span><span>DO</span><span>VR</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* PRIJZEN */}
      <section id="prijzen" className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-orange-500 text-xs font-black uppercase tracking-widest">Eerlijke prijzen</div>
            <h2 className="pb-heading text-3xl md:text-5xl font-black mt-2 text-white">Kies je pakket</h2>
            <p className="mt-3 text-slate-300 font-semibold">Vaste basisprijs + kleine seat-prijs voor elke actieve gebruiker boven het inbegrepen aantal. Geen setup-kosten. Maandelijks opzegbaar.</p>
            <p className="mt-2 text-orange-300 font-black text-sm md:text-base" data-testid="pricing-office-free-note">Je betaalt alleen voor de mensen die je op het planbord zet — eigenaar en planner zijn gratis.</p>
          </div>

          <div className="mt-6 flex justify-center">
            <div className="inline-flex border-2 border-orange-600 rounded-full p-1 bg-slate-900" data-testid="period-toggle">
              {[
                { k: "month", l: "Maand" },
                { k: "year",  l: "Jaar · 2 mnd gratis" },
              ].map((o) => (
                <button
                  key={o.k}
                  onClick={() => setPeriod(o.k)}
                  className={`px-4 py-2 rounded-full text-sm font-black uppercase tracking-widest ${period === o.k ? "bg-orange-600 text-white" : "text-slate-200"}`}
                  data-testid={`period-${o.k}`}
                >{o.l}</button>
              ))}
            </div>
          </div>

          {!plans && <div className="mt-8 text-center text-slate-400">Tarieven laden…</div>}
          {plans && (
            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {plans.tiers.map((p) => {
                const price = p.prices[period];
                const isPro = p.tier === "pro";
                const savings = calcYearlySavings(p.prices.month.base_amount, p.prices.year.base_amount);
                const startSeat = plans.tiers[0]?.prices[period]?.seat_amount || price.seat_amount;
                const seatDiscountPct = startSeat > 0 ? Math.round(((startSeat - price.seat_amount) / startSeat) * 100) : 0;
                const extras = extraSeats[p.tier] || 0;
                const perMonthDivisor = period === "year" ? 12 : 1;
                const baseMonthly = price.base_amount / perMonthDivisor;
                const seatMonthly = price.seat_amount / perMonthDivisor;
                const totalMonthly = baseMonthly + extras * seatMonthly;
                const totalPeriod = price.base_amount + extras * price.seat_amount;
                return (
                  <div key={p.tier} className={`rounded-lg border-2 p-6 relative ${isPro ? "border-orange-600 bg-slate-900 ring-4 ring-orange-600/30 shadow-[8px_8px_0_#EA580C]" : "border-slate-700 bg-slate-900"}`} data-testid={`plan-${p.tier}`}>
                    {isPro && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border-2 border-slate-950">Meest gekozen</div>}
                    <div className="pb-heading text-2xl font-black text-white">{p.name}</div>
                    <div className="mt-2">
                      <span className="pb-heading text-5xl font-black tabular-nums text-white">{fmtEUR(baseMonthly)}</span>
                      <span className="text-slate-400 font-bold text-sm"> / maand</span>
                    </div>
                    <div className="text-xs font-bold text-slate-400 mt-1">
                      {period === "year"
                        ? `Jaarlijks ${fmtEUR(price.base_amount)} · bespaart ${fmtEUR(savings)}`
                        : `of ${fmtEUR(p.prices.year.base_amount)}/jaar (2 mnd gratis)`}
                    </div>

                    {/* Seat pricing block — emphasizes the per-user discount */}
                    <div className="mt-5 rounded border-2 border-slate-700 bg-slate-950 p-3">
                      <div className="flex items-baseline justify-between">
                        <div>
                          <div className="pb-heading font-black text-3xl text-white tabular-nums">{p.included_seats}</div>
                          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Ingeplande gebruikers inbegrepen</div>
                        </div>
                        <div className="text-right">
                          <div className="pb-heading font-black text-xl text-white tabular-nums">{fmtEUR(seatMonthly)}</div>
                          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Per extra gebruiker /mnd</div>
                          {seatDiscountPct > 0 && (
                            <div className="mt-1 inline-block text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-orange-600 text-white" data-testid={`seat-discount-${p.tier}`}>
                              −{seatDiscountPct}% vs Start
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Extra seats calculator */}
                    <div className="mt-3 rounded border-2 border-orange-600/60 bg-slate-950 p-3" data-testid={`seat-calc-${p.tier}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-[10px] font-black uppercase tracking-widest text-orange-400">Extra uitvoerders</div>
                          <div className="text-xs font-semibold text-slate-300">Totaal actief: {p.included_seats + extras}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => bumpSeats(p.tier, -1)}
                            disabled={extras === 0}
                            className="w-9 h-9 rounded border-2 border-orange-600 text-orange-500 hover:bg-orange-600 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center font-black"
                            data-testid={`seat-minus-${p.tier}`}
                            aria-label="Minder seats"
                          ><Minus size={16}/></button>
                          <div className="w-10 text-center pb-heading font-black text-white tabular-nums text-lg" data-testid={`seat-count-${p.tier}`}>+{extras}</div>
                          <button
                            type="button"
                            onClick={() => bumpSeats(p.tier, 1)}
                            className="w-9 h-9 rounded border-2 border-orange-600 text-orange-500 hover:bg-orange-600 hover:text-white flex items-center justify-center font-black"
                            data-testid={`seat-plus-${p.tier}`}
                            aria-label="Meer seats"
                          ><Plus size={16}/></button>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-800 flex items-baseline justify-between">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Jouw prijs</div>
                        <div className="text-right">
                          <div className="pb-heading font-black text-2xl text-white tabular-nums" data-testid={`seat-total-${p.tier}`}>{fmtEUR(totalMonthly)}<span className="text-slate-400 font-bold text-xs">/mnd</span></div>
                          {extras > 0 && (
                            <div className="text-[10px] font-bold text-slate-400">
                              {period === "year" ? `Jaarlijks ${fmtEUR(totalPeriod)}` : `Basis + ${extras}× ${fmtEUR(seatMonthly)}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <ul className="mt-5 space-y-2 text-sm font-semibold text-slate-200">
                      {p.features.map((f) => <li key={f} className="flex gap-2"><CheckCircle2 size={16} className="text-orange-500 shrink-0 mt-0.5"/>{featureLabel(f)}</li>)}
                    </ul>
                    <Link to={`/signup?tier=${p.tier}&period=${period}${extras ? `&seats=${extras}` : ""}`} className={`mt-6 w-full flex items-center justify-center gap-2 ${isPro ? "pb-btn-primary" : "h-11 px-6 rounded font-black uppercase tracking-widest text-sm border-2 border-white text-white hover:bg-white hover:text-slate-900 transition"}`} data-testid={`cta-${p.tier}`}>
                      <Zap size={16}/> 7 dagen gratis proberen
                    </Link>
                    <div className="text-[10px] text-center text-slate-400 font-bold uppercase tracking-widest mt-2">Geen betalingsinfo vereist</div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="text-center mt-8 text-slate-400 font-semibold text-sm">Alle pakketten bevatten dezelfde functies. Het verschil zit in <span className="text-white font-black">het aantal uitvoerders</span> en de <span className="text-white font-black">korting per extra gebruiker</span>. Maandelijks opzegbaar · Geen setup-kosten · Alle prijzen exclusief btw</div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="bg-slate-900 py-14 border-y-2 border-orange-600">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="text-orange-500 text-xs font-black uppercase tracking-widest">Gebruikt door</div>
          <div className="mt-4 flex justify-center">
            <div className="pb-heading font-black tracking-widest text-lg md:text-xl border-2 border-orange-600 rounded px-5 py-2 bg-slate-950 text-white">MILURA BV</div>
          </div>
          <div className="mt-4 text-sm text-slate-300 font-semibold">Ben jij de volgende? <Link to="/signup" className="text-orange-500 underline">Start je proefperiode →</Link></div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 bg-slate-950">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center">
            <div className="text-orange-500 text-xs font-black uppercase tracking-widest">Veelgestelde vragen</div>
            <h2 className="pb-heading text-3xl md:text-5xl font-black mt-2 text-white">Nog vragen? Prima.</h2>
          </div>
          <div className="mt-8 space-y-2">
            {[
              { q: "Werkt de Vandaag-app met slecht bereik?", a: "De laatst-geladen planning, foto's en materiaal blijven zichtbaar zodra ze eenmaal in de app staan. Volledige offline-sync met foto-upload staat op de roadmap." },
              { q: "Kan ik ZZP'ers erbij plannen?", a: "Zeker. ZZP'ers krijgen een eigen account met de Uitvoerder-rol. Ze zien alleen wat je publiceert, kunnen niet in de administratie." },
              { q: "Hoe registreer ik uren?", a: "De uitvoerder start op de bouwplaats de timer in de Vandaag-app en drukt op stop als hij klaar is. Alle uren stromen automatisch naar het planbord én per project. De kantoormedewerker of eigenaar kan de uren nog aanpassen als het echt nodig is." },
              { q: "Kan ik maandelijks opzeggen?", a: "Ja, altijd. Via het Facturatie-scherm zeg je met één klik op — je houdt toegang tot het einde van de al-betaalde periode." },
              { q: "Van wie is mijn data?", a: "Van jou. Exporteer op elk moment projecten, uren en foto's als CSV/JSON. AVG-proof, servers in de EU." },
              { q: "Hoe start ik?", a: "Registreer met je e-mailadres, tik je ploeg snel in, en plan morgen al. 7 dagen gratis, zonder betalingsinfo." },
            ].map((f) => <Faq key={f.q} q={f.q} a={f.a}/>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="pb-heading text-3xl md:text-5xl font-black">Morgen al één planbord voor iedereen.</h2>
          <p className="mt-4 text-slate-300 font-semibold">Registreer nu. 7 dagen gratis, geen betalingsinfo. Ploegbord binnen 5 minuten opgezet.</p>
          <div className="mt-6 flex justify-center">
            <Link to="/signup" className="pb-btn-primary flex items-center gap-2 !min-h-[56px] !px-6 text-base" data-testid="footer-cta-signup">
              <Zap size={20}/> Start je gratis proefperiode
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-slate-400 border-t-4 border-orange-600 py-10">
        <div className="max-w-7xl mx-auto px-4 grid sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 text-sm">
          <div>
            <img src="/logo-ploegbord.webp?v=8" alt="Ploegbord" className="h-14 w-auto"/>
            <div className="mt-3 text-xs">Planning voor de Nederlandse bouw. Made in NL.</div>
          </div>
          <div>
            <div className="text-white font-black uppercase tracking-widest text-xs">Product</div>
            <ul className="mt-2 space-y-1">
              <li><a href="#kenmerken" className="hover:text-orange-400">Kenmerken</a></li>
              <li><a href="#prijzen" className="hover:text-orange-400">Prijzen</a></li>
              <li><Link to="/signup" className="hover:text-orange-400">Gratis proberen</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-black uppercase tracking-widest text-xs">Juridisch</div>
            <ul className="mt-2 space-y-1">
              <li><Link to="/privacy" className="hover:text-orange-400">Privacybeleid</Link></li>
              <li><Link to="/voorwaarden" className="hover:text-orange-400">Algemene voorwaarden</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-black uppercase tracking-widest text-xs">Contact</div>
            <ul className="mt-2 space-y-1 text-xs">
              <li><a href="mailto:info@ploegbord.nl" className="hover:text-orange-400">info@ploegbord.nl</a></li>
              <li className="text-slate-500 pt-2">Ploegbord is een product van<br/>FitByJ · Heerlen · KVK 96450261</li>
            </ul>
          </div>
          <div>
            <div className="text-white font-black uppercase tracking-widest text-xs">Vertrouwen</div>
            <ul className="mt-2 space-y-1">
              <li>AVG-proof · EU-hosting</li>
              <li>Betaalveilig via iDEAL / Wero</li>
              <li>Data van jou — exporteren wanneer je wilt</li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 mt-8 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Ploegbord</span>
        </div>
      </footer>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border-2 border-slate-700 rounded p-3 bg-slate-950">
      <div className="pb-heading font-black text-3xl text-white">{n}</div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{l}</div>
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-2 border-slate-700 rounded bg-slate-900">
      <button onClick={() => setOpen((v) => !v)} className="w-full text-left px-4 py-3 flex items-center justify-between" data-testid="faq-item">
        <span className="pb-heading font-black text-white">{q}</span>
        <span className="text-orange-500 font-black text-xl">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-4 pb-4 text-slate-300 font-semibold text-sm border-t-2 border-slate-700 pt-3">{a}</div>}
    </div>
  );
}

function featureLabel(k) {
  const M = {
    planbord: "Centraal planbord met drag-and-drop",
    vandaag: "Vandaag-app voor uitvoerders",
    uren: "Urenregistratie start/stop timer",
    uren_export: "Uren exporteren naar Excel",
    meer_seats: "Meer gebruikers inbegrepen",
    korting_seat: "Korting per extra gebruiker",
    prio_support: "Voorrangs-support",
    multi_vestiging: "Meerdere vestigingen",
    api: "API-toegang",
  };
  return M[k] || k;
}
